<template>
    <BaseContainer>
        <span slot="principal">
            <v-content class="pr-2 pl-2">
                <v-toolbar class="ma-2" style="border-radius: 30px;">
                    <v-btn icon link to="/">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-spacer/>
                    <v-toolbar-title v-show="$vuetify.breakpoint.smAndUp">Net MRR</v-toolbar-title>
                    <v-spacer/>
                    <v-btn icon :loading="carregando" :disabled="!podeConsultar" @click="consultar">
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </v-toolbar>

                <v-alert v-if="!podeConsultar" type="info" text class="ma-1">
                    Você não tem permissão para consultar este relatório.
                    Peça a um administrador a permissão
                    <strong>Zapercontrol Relatorios Net Mrr Consultar</strong>
                    em Usuários ▸ Permissões.
                </v-alert>

                <v-card v-else elevation="2" class="ma-1">
                    <v-card-text>
                        <v-progress-linear
                            v-if="carregando"
                            indeterminate
                            color="primary"
                            class="mb-2"
                        />

                        <v-row>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="formatarValor(mesAtual.net_mrr)"
                                    :label="`Net MRR ${mesAtual.mes || ''} (parcial)`"
                                    :color="corDoValor(mesAtual.net_mrr)"
                                    icon="mdi-calendar-today"
                                />
                            </v-col>
                            <v-col v-for="n in [3, 6, 12]" :key="n" cols="12" sm="6" md="3">
                                <Card
                                    :value="formatarValor(media(n))"
                                    :label="`Média dos últimos ${n} meses`"
                                    :color="corDoValor(media(n))"
                                    icon="mdi-chart-line-variant"
                                />
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Net MRR por mês
                                    </div>
                                    <div class="text-center caption text--secondary">
                                        Novo + reativação + upgrades − downgrades − churn.
                                        As médias usam só meses fechados.
                                    </div>
                                    <v-card-text>
                                        <canvas ref="chartNetMrr" height="90"></canvas>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Mês a mês
                                    </div>
                                    <v-data-table
                                        :headers="headers"
                                        :items="linhasDesc"
                                        :loading="carregando"
                                        :mobileBreakpoint="0"
                                        disable-pagination
                                        hide-default-footer
                                        disable-sort
                                        dense
                                    >
                                        <template v-slot:item.mes="{ item }">
                                            {{ item.mes }}<span v-if="item.parcial" class="text--secondary"> (parcial)</span>
                                        </template>
                                        <template v-slot:item.novo="{ item }">R$ {{ formatPrice(item.novo) }}</template>
                                        <template v-slot:item.reativacao="{ item }">R$ {{ formatPrice(item.reativacao) }}</template>
                                        <template v-slot:item.expansao="{ item }">R$ {{ formatPrice(item.expansao) }}</template>
                                        <template v-slot:item.contracao="{ item }">
                                            <span :class="{ 'red--text': +item.contracao > 0 }">{{ formatarValor(-item.contracao) }}</span>
                                        </template>
                                        <template v-slot:item.churn="{ item }">
                                            <span :class="{ 'red--text': +item.churn > 0 }">{{ formatarValor(-item.churn) }}</span>
                                        </template>
                                        <template v-slot:item.net_mrr="{ item }">
                                            <strong :class="+item.net_mrr < 0 ? 'red--text' : 'green--text'">
                                                {{ formatarValor(item.net_mrr) }}
                                            </strong>
                                        </template>
                                    </v-data-table>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-content>
        </span>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer.vue';
import Card from '@/views/home/Card.vue';
import Chart from 'chart.js';

export default {
    name: 'RelatorioNetMrr',
    components: { BaseContainer, Card },

    data: () => ({
        carregando: false,
        linhas: [],
        chart: null,

        cores: {
            novo: '#7CB342',
            reativacao: '#C5E1A5',
            expansao: '#42A5F5',
            contracao: '#FFB74D',
            churn: '#E57373',
            net: '#37474F',
        },

        headers: [
            { text: 'Mês', value: 'mes' },
            { text: 'Novo MRR', value: 'novo', align: 'end' },
            { text: 'Reativação', value: 'reativacao', align: 'end' },
            { text: 'Upgrades', value: 'expansao', align: 'end' },
            { text: 'Downgrades', value: 'contracao', align: 'end' },
            { text: 'Churn', value: 'churn', align: 'end' },
            { text: 'Net MRR', value: 'net_mrr', align: 'end' },
        ],
    }),

    computed: {
        podeConsultar() {
            return this.checkPermission('zapercontrol_relatorios_net_mrr_consultar');
        },

        mesAtual() {
            return this.linhas.find(l => l.parcial) || {};
        },

        mesesFechados() {
            return this.linhas.filter(l => !l.parcial);
        },

        linhasDesc() {
            return [...this.linhas].reverse();
        },
    },

    watch: {
        podeConsultar(pode) {
            if (pode) this.consultar();
        },
    },

    mounted() {
        this.consultar();
    },

    beforeDestroy() {
        this.chart && this.chart.destroy();
    },

    methods: {
        async consultar() {
            if (!this.podeConsultar) {
                return;
            }

            this.carregando = true;

            try {
                const { data } = await this.$http.get('relatorios/net-mrr/mensal');
                this.linhas = data.data || [];
                this.$nextTick(() => this.gerarGrafico());
            } catch (e) {
                console.error(e);
                this.notify('Erro ao carregar o relatório de Net MRR', 'warning');
            } finally {
                this.carregando = false;
            }
        },

        // Média do Net MRR dos últimos n meses fechados.
        media(n) {
            const meses = this.mesesFechados.slice(-n);

            if (!meses.length) {
                return null;
            }

            return meses.reduce((acc, l) => acc + Number(l.net_mrr), 0) / meses.length;
        },

        formatarValor(valor) {
            if (valor === null || valor === undefined) {
                return '—';
            }

            const sinal = Number(valor) < 0 ? '− ' : '';
            return `${sinal}R$ ${this.formatPrice(Math.abs(valor))}`;
        },

        corDoValor(valor) {
            if (valor === null || valor === undefined) {
                return 'blue-grey';
            }

            return Number(valor) < 0 ? 'red darken-2' : 'green darken-1';
        },

        gerarGrafico() {
            const ctx = this.$refs.chartNetMrr;

            if (!ctx) {
                return;
            }

            this.chart && this.chart.destroy();

            const serie = campo => this.linhas.map(l => Number(l[campo]));
            const negativa = campo => this.linhas.map(l => -Number(l[campo]));

            this.chart = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels: this.linhas.map(l => (l.parcial ? `${l.mes}*` : l.mes)),
                    datasets: [
                        {
                            type: 'line',
                            label: 'Net MRR',
                            data: serie('net_mrr'),
                            borderColor: this.cores.net,
                            backgroundColor: this.cores.net,
                            fill: false,
                            lineTension: 0.3,
                            order: 0,
                        },
                        { label: 'Novo MRR', data: serie('novo'), backgroundColor: this.cores.novo, stack: 'mrr', order: 1 },
                        { label: 'Reativação', data: serie('reativacao'), backgroundColor: this.cores.reativacao, stack: 'mrr', order: 1 },
                        { label: 'Upgrades', data: serie('expansao'), backgroundColor: this.cores.expansao, stack: 'mrr', order: 1 },
                        { label: 'Downgrades', data: negativa('contracao'), backgroundColor: this.cores.contracao, stack: 'mrr', order: 1 },
                        { label: 'Churn', data: negativa('churn'), backgroundColor: this.cores.churn, stack: 'mrr', order: 1 },
                    ],
                },
                options: {
                    responsive: true,
                    scales: {
                        xAxes: [{ stacked: true }],
                        yAxes: [{
                            stacked: true,
                            ticks: { callback: v => this.formatarValor(v) },
                        }],
                    },
                    tooltips: {
                        mode: 'index',
                        intersect: false,
                        callbacks: {
                            label: (item, data) => `${data.datasets[item.datasetIndex].label}: ${this.formatarValor(item.yLabel)}`,
                        },
                    },
                },
            });
        },
    },
}
</script>
